import { styled } from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { titleize } from 'lib/helpers';
import React from 'react';

export type StatusColor = 'green' | 'yellow' | 'red' | 'grey' | 'purple';
export type StatusColorMap = Record<string, StatusColor>;

const PREFIX = 'StatusCell';

const classes = {
    green: `${PREFIX}-green`,
    yellow: `${PREFIX}-yellow`,
    red: `${PREFIX}-red`,
    grey: `${PREFIX}-grey`,
    purple: `${PREFIX}-purple`
};

const Root = styled('span')(({ theme }) => ({
    [`&.${classes.green}`]: { color: green[500] },
    [`&.${classes.yellow}`]: { color: orange[500] },
    [`&.${classes.red}`]: { color: red[400] },
    [`&.${classes.grey}`]: { color: theme.palette.grey[500] },
    [`&.${classes.purple}`]: { color: theme.palette.purple.core }
}));

interface StatusCellProps {
    value: string;
    colorMap: StatusColorMap;
    defaultColor?: StatusColor;
    labelMap?: Record<string, string>;
}

export const StatusCell: React.FC<StatusCellProps> = React.memo(
    ({ value, colorMap, defaultColor = 'grey', labelMap }) => {
        const className = classes[colorMap[value] ?? defaultColor];
        const label = React.useMemo(
            () => titleize(String(labelMap?.[value] ?? value), true).replaceAll('_', ' '),
            [value, labelMap]
        );
        return <Root className={className}>{label}</Root>;
    }
);

export function renderStatusCell(
    colorMap: StatusColorMap,
    defaultColor?: StatusColor,
    labelMap?: Record<string, string>
): (params: GridRenderCellParams) => React.ReactNode {
    return (params: GridRenderCellParams) => (
        <StatusCell
            value={params.value}
            colorMap={colorMap}
            defaultColor={defaultColor}
            labelMap={labelMap}
        />
    );
}
